import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ClassNames } from '../../../constants/class-names.constants';
import { DateFnsFormat, DateFormat } from '../../../constants/date-format.constants';
import { Table } from '../../../constants/table.constants';
import { LsqBaseColumnComponent } from './base-column.component';

@Component({
    selector: 'lsq-date-column',
    template: `
        <ng-container [cdkColumnDef]="column.key">
            <th
                cdk-header-cell
                *cdkHeaderCellDef
                [ngStyle]="{ width: column.width || 'auto', maxWidth: column.maxWidth || 'auto' }"
                [ngClass]="[
                    column.sortable !== false ? classNames.Sortable : 'not-sortable',
                    alignRight ? classNames.AlignRight : '',
                    !!column.headerPopoverContent ? classNames.ThWithPopover : '',
                    column.className || ''
                ]"
                (click)="changeSort()"
            >
                <div class="th-content">
                    <ng-container *ngIf="column.headerPopoverContent">
                        <popover-content
                            #headerPopover
                            [size]="column.headerPopoverSize || 'small'"
                        >
                            {{ column.headerPopoverContent }}
                        </popover-content>
                        <div
                            [popover]="headerPopover"
                            [popoverPlacement]="getHeaderPopoverPlacement()"
                        >
                            <span [innerHtml]="column.header"></span>
                        </div>
                    </ng-container>

                    <ng-container *ngIf="!column.headerPopoverContent">
                        <span [innerHtml]="column.header"></span>
                    </ng-container>

                    <div *ngIf="isSortable$ | async">
                        <lsq-icon
                            class="sort-caret"
                            [ngClass]="{ 'is-sorted': isCurrentlySorted() }"
                            [type]="getIconType()"
                            [color]="getIconColor()"
                        >
                        </lsq-icon>
                    </div>
                </div>
            </th>

            <td
                cdk-cell
                *cdkCellDef="let row"
                [ngStyle]="{ width: column.width || 'auto', maxWidth: column.maxWidth || 'auto' }"
                [ngClass]="[
                    column.sortable !== false ? classNames.Sortable : 'not-sortable',
                    alignRight ? classNames.AlignRight : '',
                    column.onCellClick &&
                    (!column.isClickableFunction || column.isClickableFunction(row))
                        ? classNames.CellClickable
                        : '',
                    column.className || ''
                ]"
                (click)="
                    column.onCellClick &&
                    (!column.isClickableFunction || column.isClickableFunction(row))
                        ? column.onCellClick(row)
                        : undefined
                "
            >
                <ng-container *ngIf="!isDateTimeMultiline">
                    <ng-container
                        *ngIf="
                            row[column.key] === nullDate ||
                                row[column.key] === protoNullDate ||
                                !row[column.key] ||
                                shouldDisplayNull(row);
                            else days
                        "
                    >
                        {{ nullDisplay }}
                    </ng-container>
                    <ng-template #days>
                        <ng-container *ngIf="isDays; else dateTime">
                            {{ row[column.key] | days }}
                        </ng-container>
                    </ng-template>
                    <ng-template #dateTime>
                        <ng-container *ngIf="isDateTime; else shortDate">
                            {{ row[column.key] | date: dateFormat.DateTime }}
                        </ng-container>
                    </ng-template>
                    <ng-template #shortDate>
                        <ng-container *ngIf="isShortDate; else default">
                            {{ row[column.key] | lsqDate: dateFormat.LsqShort }}
                        </ng-container>
                    </ng-template>
                    <ng-template #default>
                        <ng-container
                            *ngIf="
                                !!row[column.key] &&
                                row[column.key] !== nullDate &&
                                !isDays &&
                                !isDateTime &&
                                !isShortDate
                            "
                        >
                            {{ row[column.key] | lsqDate: 'shortDate' }}
                        </ng-container>
                    </ng-template>
                </ng-container>

                <ng-container *ngIf="isDateTimeMultiline">
                    <ng-container
                        *ngIf="
                            row[column.key] === nullDate ||
                                row[column.key] === protoNullDate ||
                                !row[column.key] ||
                                shouldDisplayNull(row);
                            else multilineDate
                        "
                    >
                        <span class="multiline-date">{{ nullDisplay }}</span>
                    </ng-container>
                    <ng-template #multilineDate>
                        <ng-container *ngIf="!!row[column.key]">
                            <span class="multiline-date">
                                {{ row[column.key] | date: dateFormat.LsqShortYear }}
                                <br /><span class="multiline-date-time-separator">at</span>
                                <span class="multiline-date-time">
                                    {{ row[column.key] | dateFns: dateFnsFormat.TimeFormat }}
                                </span>
                            </span>
                        </ng-container>
                    </ng-template>
                </ng-container>
            </td>

            <td cdk-footer-cell *cdkFooterCellDef [ngStyle]="{ width: column.width || 'auto' }">
                <!-- Render empty footer cell if no custom is provided. -->

                <!-- Custom Column Cell Footer -->
                <div *ngFor="let customFooter of customColumnFooters">
                    <ng-container
                        *ngTemplateOutlet="customFooter.templateRef; context: { row: row }"
                    ></ng-container>
                </div>
            </td>
        </ng-container>
    `,
    styleUrls: ['./date-column.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LsqDateColumnComponent extends LsqBaseColumnComponent implements OnInit {
    @Input() public isDateTime = false;
    @Input() public isDateTimeMultiline = false;
    @Input() public isDays = false;
    @Input() public isShortDate = false;
    @Input() public displayNullIfFieldIsTrue?: string;
    @Input() public nullDisplay = Table.NullAmount;
    public classNames = ClassNames;
    public nullDate = '0001/01/01 00:00:00';
    public protoNullDate = '1970-01-01T00:00:00Z';
    public dateFormat = DateFormat;
    public dateFnsFormat = DateFnsFormat;

    public shouldDisplayNull(row: any): boolean {
        return !!this.displayNullIfFieldIsTrue && row[this.displayNullIfFieldIsTrue] === true;
    }
}
